import axios from "./AxiosClient";
import { Constants } from "@socialcoach/services";
import { scCatchServiceException } from "../../annotations";
import { Page, Blog, CTA } from "./dtos";
import { CancelRequestOptions, ExtraFieldsParam } from ".";

const BLOG_SERVICE = `${Constants.API_URL}/bedrock/blogs`;

export type GetBlogsFilters = {
  search?: string;
  playerId?: number | null;
  contentPageId?: number | null;
  status?: Blog.Status;
};

export type GetBlogsParams = GetBlogsFilters & {
  page?: number;
  perPage?: number;
  sort?: "created_date" | "updated_date" | "published_at";
  order?: "asc" | "desc";
  extraFields?: ExtraFieldsParam;
};

export type CreateBlogRequest = {
  contentPageId: number;
};

export type UpdateBlogRequest = {
  title?: string;
  subtitle?: string;
  metaDescription?: string;
  excerpt?: string;
  tags?: string[];
  body?: Blog.Body;
  themeId?: number | null;
  callToAction?: CTA | null;
};

const defaultExtraFields: ExtraFieldsParam = {
  processingSteps: true,
  permittedEvents: true,
  author: true,
  contentPage: true,
  contentPageTheme: true,
  effectiveContentPageTheme: true,
  totalLinkClicks: true,
};

export class BlogServices {
  @scCatchServiceException()
  static async createBlog(
    request: CreateBlogRequest,
    extraFields: ExtraFieldsParam = defaultExtraFields
  ): Promise<Blog> {
    const { data } = await axios.post<Blog>(BLOG_SERVICE, request, { params: { extraFields } });
    return data;
  }

  @scCatchServiceException()
  static async getBlogs(params: GetBlogsParams = {}, options?: CancelRequestOptions): Promise<Page<Blog>> {
    const effectiveParams = { ...params } as any;
    if (effectiveParams.playerId === -1) effectiveParams.playerId = "";

    const { data } = await axios.get<Page<Blog>>(BLOG_SERVICE, { params: effectiveParams, ...options });
    return data;
  }

  @scCatchServiceException()
  static async getBlog(blogId: number, extraFields: ExtraFieldsParam = defaultExtraFields): Promise<Blog> {
    const { data } = await axios.get<Blog>(`${BLOG_SERVICE}/${blogId}`, { params: { extraFields } });
    return data;
  }

  @scCatchServiceException()
  static async updateBlog(
    blogId: number,
    request: UpdateBlogRequest,
    extraFields: ExtraFieldsParam = defaultExtraFields
  ): Promise<Blog> {
    const { data } = await axios.patch<Blog>(`${BLOG_SERVICE}/${blogId}`, request, { params: { extraFields } });
    return data;
  }

  @scCatchServiceException()
  static async publishBlog(blogId: number, extraFields: ExtraFieldsParam = defaultExtraFields): Promise<Blog> {
    const { data } = await axios.post<Blog>(`${BLOG_SERVICE}/${blogId}/publish`, {}, { params: { extraFields } });
    return data;
  }

  @scCatchServiceException()
  static async unpublishBlog(blogId: number, extraFields: ExtraFieldsParam = defaultExtraFields): Promise<Blog> {
    const { data } = await axios.post<Blog>(`${BLOG_SERVICE}/${blogId}/unpublish`, {}, { params: { extraFields } });
    return data;
  }

  @scCatchServiceException()
  static async regenerateBlog(blogId: number, extraFields: ExtraFieldsParam = defaultExtraFields): Promise<Blog> {
    const { data } = await axios.post<Blog>(`${BLOG_SERVICE}/${blogId}/regenerate`, {}, { params: { extraFields } });
    return data;
  }

  @scCatchServiceException()
  static async archiveBlog(blogId: number): Promise<void> {
    await axios.delete<void>(`${BLOG_SERVICE}/${blogId}`);
  }
}
